import './graphical-legend-part.css';

import { unByKey } from 'ol/Observable';
import { containsExtent, getForViewAndSize } from 'ol/extent';
import ImageWMS from 'ol/source/ImageWMS';
import h from 'hyperscript';
import lock from '@disy/cadenza-icons/lock.svg';

import { createToggletip } from 'ui/toggletip/toggletip';

import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { logger } from 'cadenza/utils/logging';
import { loadImage, loadImageAsObjectUrl } from 'cadenza/utils/load-image';
import { context } from 'cadenza/map/context';
import { MapProperty } from 'cadenza/map/map-property';
import { LayerProperty } from 'cadenza/map/layer-property';
import { getLayerTasks } from 'cadenza/map/layer-tasks';
import { singleExecution } from 'cadenza/api-client/api';
import { icon } from 'cadenza/utils/icon/icon';
import { getLegendStructure } from 'cadenza/api-client/maps-api';
import { getWorkbookLegendStructure, parseWorkbookUrl } from 'cadenza/api-client/workbook-api';
import { getRenderedLegendElementRow } from 'cadenza/map/legend/legend-element-row-renderer';

import i18n from './messages.properties';

const COMPONENT_NAME = 'd-graphical-legend-part';

export class GraphicalLegendPart extends HTMLElement {

  #layer;
  #layerTasks;
  #map;
  #legendFromCadenzaServer;
  _lastExtentAndZoom;
  #eventKeys;
  #image;
  #legendLoadTaskId;
  #master;
  #shouldRegisterTasks;
  #legendLoadedListeners = [];
  _isClientSideRendered;

  /**
   * Constructor
   *
   * @param {object} layer - the layer the legend image is for
   * @param {object} map - the map the graphical legend part belongs to
   * @param {object} [options] - Options
   * @param {boolean} options.master - If another GraphicalLegendPart is provided as master, the legend image is obtained from there to
   *     avoid loading it twice. Ignored for external legend image urls as those are not loaded via AJAX.
   * @param {boolean} options.isClientSideRendered - Whether the graphical legend part is rendered on the client or not
   */
  constructor (
    layer,
    map,
    { master, isClientSideRendered } = {}) {
    super();
    this.#layer = layer;
    this.#map = map;
    this.#layerTasks = getLayerTasks(layer);
    this.#legendFromCadenzaServer = layer.get(LayerProperty.LEGEND_URL).startsWith('/');
    this.#master = this.#legendFromCadenzaServer && master ? master : null;
    this.#shouldRegisterTasks = master == null;
    this._isClientSideRendered = isClientSideRendered || layer.get(LayerProperty.STRUCTURED_LEGEND_LAYER);
    // The empty "alt" attribute makes screenreaders ignore this image,
    // because we cannot provide a decent text alternative.
    // See https://www.w3.org/WAI/tutorials/images/decorative/
    this.#image = h('img.d-graphical-legend-part--image', { alt: '' });
    this.append(this.#image);
    if (map.get(MapProperty.SKETCH_LAYER)) {
      this.append(this._createLockToggletip());
    }

    this._updateImageAndMessages = singleExecution(this._updateImageAndMessages.bind(this), this.#legendFromCadenzaServer);

    if (!this.#master) {
      map.getLayers().on('remove', ({ element: removedLayer }) => {
        if (removedLayer.get(LayerProperty.ID) === layer.get(LayerProperty.ID)) {
          this._updateImageAndMessages.cancelCurrent();
        }
      });
    }
    this.hidden = true;
  }

  _createLockToggletip () {
    return createToggletip({
      toggletip: i18n('layer.image.layerLocked'),
      icon: icon(lock),
      styleClass: 'd-map-layer--locked'
    });
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    if (this.#master) {
      this._updateImageFromMasterGraphicalLegendPart();
    } else {
      this._updateImageAndMessages(false);
      this.#eventKeys = [
        this.#layer.on(`change:${LayerProperty.NAME}`, () => this._onChange(true)),
        this.#layer.on(`change:${LayerProperty.LEGEND_URL}`, () => {
          this._onChange(true);
        }),
        this.#layer.on(`change:${LayerProperty.VISIBLE}`, () => this._onChange(true)),
        this.#layer.getSource().on('change', () => this._onChange(true)),
        this.#map.on(`change:${MapProperty.SELECTED_LAYER}`, () => this._onChange(false)),
        this.#map.on('moveend', () => this._onChange(false))
      ];
    }
  }

  _updateImageFromMasterGraphicalLegendPart () {
    if (this.#master.isClientSideRendered()) {
      this.#master.addLegendLoadedListener(layerLegendItems => {
        this._updateClientSideLayerLegend(layerLegendItems);
      });
    } else {
      if (this.#master._getImageUrl()) {
        this._setImageUrl(this.#master._getImageUrl());
      }
      this.#master.addLegendLoadedListener(imageUrl => {
        this._setImageUrl(imageUrl);
      });
    }
  }

  disconnectedCallback () {
    unByKey(this.#eventKeys);
  }

  _setImageUrl (imageUrl) {
    if (this._isHidpi()) {
      this.#image.srcset = `${imageUrl} ${window.devicePixelRatio}x`;
    } else {
      this.#image.src = imageUrl;
    }
  }

  _onChange (forceReload) {
    if (context(this.#map).layers.includes(this.#layer)) {
      this._updateImageAndMessages(forceReload);
    }
  }

  _updateImageAndMessages (forceReload, signal) {
    if (!this.#layer.getVisible()) {
      // if layer is not visible there is no sense in querying the server for an image even if forced
      return Promise.resolve();
    }
    const hasEmptyRequiredCondition = this.#layer.get(LayerProperty.HAS_EMPTY_REQUIRED_CONDITIONS);
    if (hasEmptyRequiredCondition) {
      return Promise.resolve();
    }
    if (!forceReload && !this._isUpdateNeeded(this._lastExtentAndZoom)) {
      return Promise.resolve();
    }

    this._setLegendLoading(true, () => this._updateImageAndMessages.cancelCurrent());

    const updatePromise = this.#legendFromCadenzaServer
      ? this._updateImageFromCadenzaServer(signal)
      : this._updateExternalLegend(signal);
    return updatePromise
      .catch(error => {
        if (error.name !== 'AbortError') {
          logger.error('Failed to load legend for layer.', error);
        }
      })
      .finally(() => this._setLegendLoading(false));
  }

  _updateExternalLegend (signal) {
    return loadImage({
      img: this.#image,
      src: createExternalLegendUrl(this.#layer.get(LayerProperty.LEGEND_URL)),
      signal
    });
  }

  _updateImageFromCadenzaServer (signal) {
    const mapView = this.#map.getView();
    const { mapSize, extent } = getExtentAndSize(this.#layer, mapView, this.#map.getSize());
    this._lastExtentAndZoom = { extent, zoom: mapView.getZoom() };

    return this._loadAndUpdateImageFromServer({
      mapSize,
      extent,
      srsCode: mapView.getProjection().getCode(),
      signal
    });
  }

  _loadAndUpdateImageFromServer ({ mapSize, extent, srsCode, signal }) {
    const workbookIdAndHash = parseWorkbookUrl(location.pathname)?.workbookIdAndHash;
    if (this._isClientSideRendered
      && (!workbookIdAndHash || this._isDataViewLayer())) { // when workbook then only for dataView layers
      const options = {
        layerId: this.#layer.get(LayerProperty.ID),
        mapSize,
        extent,
        srsCode
      };
      return this._getLegendStructure(options, workbookIdAndHash, signal)
        .then(layerLegendItems => {
          this._updateClientSideLayerLegend(layerLegendItems);
          this._notifyLoaded(layerLegendItems);
        });
    } else {
      const url = this._buildGetLegendRequestUrl({
        layerLegendUrl: this.#layer.get(LayerProperty.LEGEND_URL),
        mapSize,
        extent,
        srsCode
      });
      return loadImageAsObjectUrl(url, signal)
        .then(({ objectUrl }) => {
          this._updateImage(objectUrl);
          this._notifyLoaded(this._getImageUrl());
        });
    }
  }

  _updateClientSideLayerLegend (layerLegendItems) {
    if (layerLegendItems && layerLegendItems.length) {
      const layerLegendRowElements = this._createLegendFromLegendStructure(layerLegendItems);
      this.innerHTML = '';
      this.append(...layerLegendRowElements);
    }
  }

  _createLegendFromLegendStructure (layerLegendItems) {
    return layerLegendItems.map(layerLegendElement => {
      return getRenderedLegendElementRow(layerLegendElement);
    });
  }

  _isUpdateNeeded ({ extent, zoom } = {}) {
    const currentViewPortIsIncludedInLastRequestedExtent = extent && containsExtent(extent, this._getViewportExtent());

    const hasZoomChanged = zoom && this.#map.getView().getZoom() !== zoom;

    return (this.#layer.getVisible() && this.#layer.getSource())
      && (hasZoomChanged || !currentViewPortIsIncludedInLastRequestedExtent);
  }

  isClientSideRendered () {
    return this._isClientSideRendered;
  }

  _getViewportExtent () {
    const view = this.#map.getView();
    const mapCenter = view.getState().center;
    return getForViewAndSize(mapCenter, view.getResolution(), view.getRotation(), this.#map.getSize());
  }

  _buildGetLegendRequestUrl ({ layerLegendUrl, extent, mapSize: [ width, height ], srsCode }) {
    const url = new URL(cadenzaUrl(layerLegendUrl), location.origin);
    url.searchParams.append('WIDTH', width);
    url.searchParams.append('HEIGHT', height);
    url.searchParams.append('BBOX', extent.join());
    url.searchParams.append('SRS', srsCode);
    if (this._isHidpi()) {
      url.searchParams.append('DPI', 96 * window.devicePixelRatio);
    }
    return url.toString();
  }

  _isHidpi () {
    return this.#legendFromCadenzaServer;
  }

  _setLegendLoading (loading, cancelCallback) {
    if (this.#legendLoadTaskId) {
      this.#layerTasks.completeTask(this.#legendLoadTaskId);
      this.#legendLoadTaskId = null;
    }
    if (loading && this.#shouldRegisterTasks) {
      this.#legendLoadTaskId = this.#layerTasks.registerTask(
        cancelCallback,
        () => {
          this._updateImageAndMessages(true);
        });
    }
  }

  _updateImage (imageObjectUrl) {
    this._freeMemoryOfCurrentImageSrcObjectUrl();
    this._setImageUrl(imageObjectUrl);
  }

  _freeMemoryOfCurrentImageSrcObjectUrl () {
    if (this._isHidpi()) {
      if (this.#image.srcset) {
        window.URL.revokeObjectURL(this._getImageUrl());
      }
    } else {
      if (this.#image) {
        window.URL.revokeObjectURL(this.#image.src);
      }
    }
  }

  _notifyLoaded (payload) {
    for (const listener of this.#legendLoadedListeners) {
      listener(payload);
    }
  }

  _getImageUrl () {
    return this._isHidpi() ? this.#image.srcset.split(' ')[0] : this.#image.src;
  }

  addLegendLoadedListener (listener) {
    this.#legendLoadedListeners.push(listener);
  }

  _getLegendStructure (options, workbookIdAndHash, signal) {
    if (workbookIdAndHash) {
      return getWorkbookLegendStructure({ ...options, workbookIdAndHash }, signal);
    } else {
      return getLegendStructure(options, signal);
    }
  }

  _isDataViewLayer () {
    return !this.#layer.get(LayerProperty.LEGEND_URL).startsWith('/api/legend');
  }

}

function createExternalLegendUrl (urlString) {
  const url = new URL(urlString);
  url.searchParams.append('_', Date.now());
  return cadenzaUrl(url.toString());
}

function getExtentAndSize (layer, mapView, mapSize) {
  const viewState = mapView.getState();
  const extentRatio = (layer.getSource() instanceof ImageWMS) ? layer.getSource().ratio_ : 1.0;
  const requestSizePixels = [
    Math.ceil(extentRatio * mapSize[0]),
    Math.ceil(extentRatio * mapSize[1])
  ];

  // CADENZA-10130: We use viewState.center so we get the exact same map center
  // that is used in OpenLayer WMS source to load the map layer itself
  const requestExtent = getForViewAndSize(viewState.center, mapView.getResolution(), mapView.getRotation(), requestSizePixels);

  return { mapSize: requestSizePixels, extent: requestExtent };
}

customElements.define(COMPONENT_NAME, GraphicalLegendPart);
